/* eslint-disable sonarjs/no-identical-functions */
import type { Route } from 'vue-router';
import Router from 'vue-router';
import Segment from '@getaccept/lib-shared/src/segment';
import { UserRoleLegacy } from '@getaccept/lib-shared/src/users/types/user-role-legacy';
import { PathName } from '@getaccept/lib-shared/src/enums/PathNames';
import { ModuleName } from '@getaccept/lib-shared/src/entity/app-modules/enums/app-module';
import { getLocaleFromCountryCode } from '@getaccept/lib-shared/src/helpers/translation.helper';
import { Feature } from '@getaccept/feature-restriction';
import { useLaunchDarkly } from '@getaccept/lib-shared/src/launch-darkly/launch-darkly.composable';
import { ChatConstants } from '@getaccept/dsr-shared/src/chat/constants/chat-constants';
import { useSessionStore } from '../session/store/session.store';
import Dashboard from '../dashboard/components/Dashboard.vue';
import DashboardNew from '../dashboard/new/components/Dashboard.vue';
import i18n from '../lang';
import RoomsConstants from '../rooms/constants/rooms.constants';
import { useRequiredAppDataStore } from '../stores/required-app-data.store';
import { RouterHelper } from '../router/helpers/router.helper';
import { useModulesStore } from '../modules/store/modules.store';
import { useAccessDeniedModalStore } from '../access-denied/access-denied-modal.store';
import { useRouterHelper } from '../router/composables/router-helper.composable';
import { useSubscriptionStore } from '../subscription/store/subscription.store';
import { LibraryConstants } from '../library/constants/library-constants';

const loadDealroomView = () =>
  import(/* webpackChunkName: "dsr-edit" */ '@getaccept/dsr-edit-site').then(
    ({ DsrEdit }) => DsrEdit
  );

const router = new Router({
  mode: 'history',
  base: '/',
  linkExactActiveClass: 'selected',
  linkActiveClass: 'selected',
  routes: [
    {
      name: PathName.Root,
      path: '/',
      beforeEnter: (to, _from, next) => {
        const { defaultPathName } = useRouterHelper();
        next({ name: defaultPathName.value, query: to.query });
      },
    },
    {
      name: PathName.Intro,
      path: '/intro',
      redirect: { name: PathName.Root, query: { intro: null } },
    },
    {
      name: PathName.Dashboard,
      path: '/dashboard',
      meta: {
        title: 'Dashboard',
        access: { module: ModuleName.Contracts },
      },
      component: Dashboard,
    },
    {
      name: PathName.NewDashboard,
      path: '/new-dashboard',
      meta: {
        title: 'New dashboard',
        access: { module: ModuleName.Contracts },
      },
      component: DashboardNew,
    },
    {
      name: PathName.Dealboard,
      path: '/dealboard/:dealType?',
      meta: {
        title: 'Dealboard',
        access: { module: ModuleName.Contracts, features: [Feature.Dealboard] },
      },
      component: () =>
        import(/* webpackChunkName: "dealboard" */ '../dealboard/components/Dealboard.vue'),
      props: true,
    },
    {
      name: PathName.Rooms,
      path: '/rooms',
      meta: { title: 'Rooms', access: { module: ModuleName.DealRooms } },
      beforeEnter: (_, from: Route, next) => {
        if (from.name !== PathName.DsrEdit) {
          localStorage.setItem('roomsFilter', JSON.stringify(RoomsConstants.defaultFilter));
        }
        next();
      },
      component: () => import(/* webpackChunkName: "rooms" */ '../rooms/components/Rooms.vue'),
      props: true,
    },
    {
      name: PathName.DocumentEdit,
      meta: { title: 'Document', access: { module: ModuleName.Contracts } },
      path: '/document/edit/:documentId',
      props: true,
      component: () =>
        import(
          /* webpackChunkName: "document-edit" */ '../document-edit/components/DocumentEdit.vue'
        ),
    },
    {
      name: PathName.DocumentView,
      meta: { title: 'Document', access: { module: ModuleName.Contracts } },
      path: '/document/view/:documentId',
      props: true,
      component: () =>
        import(
          /* webpackChunkName: "document-view" */ '../document-view/components/DocumentView.vue'
        ),
    },
    {
      name: PathName.Documents,
      path: '/document/:filter?',
      meta: { title: 'Documents', access: { module: ModuleName.Contracts } },
      component: () =>
        import(/* webpackChunkName: "documents" */ '../documents/components/Documents.vue'),
    },
    {
      name: PathName.ContentLibrary,
      meta: { title: 'Content Library', access: { module: ModuleName.Contracts } },
      path: '/content-library',
      component: () =>
        import(
          /* webpackChunkName: "content-library" */ '../content-library/components/ContentLibrary.vue'
        ),
      props: route => ({
        folderId: route.query.folderId,
      }),
    },
    {
      name: PathName.Library,
      meta: { title: 'Library', access: { module: ModuleName.DealRooms } },
      path: '/library',
      component: () =>
        import(/* webpackChunkName: "dsr-library" */ '../library/components/Library.vue'),
      beforeEnter: (_, from: Route, next) => {
        if (from.name !== PathName.DrTemplate && from.name !== PathName.DrResource) {
          sessionStorage.setItem('libraryFilter', JSON.stringify(LibraryConstants.defaultFilter));
        }
        next();
      },
      props: route => ({
        directory: route.query.directory,
      }),
    },
    {
      meta: { title: 'Resource Details', access: { module: ModuleName.Contracts } },
      path: '/content-library/details/:resourceId',
      component: () =>
        import(
          /* webpackChunkName: "resource-details" */ '../content-library/resource-details/components/ResourceDetails.vue'
        ),
      props: true,
    },
    {
      name: PathName.ContentLibraryEdit,
      meta: { title: 'Create resource', access: { module: ModuleName.Contracts } },
      path: '/content-library/edit/:type/:resourceId?',
      component: () =>
        import(
          /* webpackChunkName: "create-resource" */ '../content-library/create-resource/components/CreateResource.vue'
        ),
      props: true,
    },
    {
      name: PathName.Archive,
      path: '/archive',
      meta: {
        title: 'Archive',
        access: { module: ModuleName.Contracts, features: [Feature.Archive] },
      },
      component: () =>
        import(/* webpackChunkName: "contracts  " */ '../contracts/components/Contracts.vue'),
      props: route => ({ isArchive: true, folderId: route.query.folderId }),
    },
    {
      path: '/contract-management/:folderId?',
      redirect: to => ({
        name: PathName.Contracts,
        query: { folderId: to.params.folderId || to.query.folderId },
      }),
    },
    {
      name: PathName.Contracts,
      path: '/contracts',
      meta: {
        title: 'Contracts',
        access: { module: ModuleName.Contracts, features: [Feature.ContractManagement] },
      },
      props: route => ({
        folderId: route.query.folderId,
        searchMode: route.query.searchMode,
        search: route.query.search,
      }),
      component: () =>
        import(/* webpackChunkName: "contracts  " */ '../contracts/components/Contracts.vue'),
    },
    {
      name: PathName.ProductLibrary,
      path: '/products',
      meta: {
        title: 'Product library',
        access: { userRoles: [UserRoleLegacy.Admin], features: [Feature.ProductLibrary] },
      },
      component: () =>
        import(
          /* webpackChunkName: "product-library" */ '../product-library/components/ProductLibrary.vue'
        ),
    },
    {
      path: '/report/exported',
      redirect: { name: PathName.Reports, params: { tab: 'export' } },
    },
    {
      name: PathName.Reports,
      path: '/report/:tab?',
      meta: {
        title: 'Reports',
        access: { module: ModuleName.Contracts, features: [Feature.Reports] },
      },
      component: () => import(/* webpackChunkName: "reports" */ '../reports/components/Report.vue'),
      props: true,
    },
    {
      name: PathName.Contact,
      meta: { title: 'Contact' },
      path: '/contact/show/:contactId',
      component: () =>
        import(/* webpackChunkName: "contacts" */ '../contacts/components/Contacts.vue'),
      props: true,
    },
    {
      name: PathName.Contacts,
      path: '/contact/:filter?',
      meta: { title: 'Contacts' },
      component: () =>
        import(/* webpackChunkName: "contacts" */ '../contacts/components/Contacts.vue'),
    },
    {
      name: PathName.Settings,
      path: '/settings',
      meta: { title: 'Settings' },
      component: () => import(/* webpackChunkName: "settings" */ '../views/Settings.vue'),
      children: [
        {
          name: PathName.SettingsUserProfile,
          path: 'user-profile',
          component: () =>
            import(
              /* webpackChunkName: "settings" */ '../settings/profile/components/UserProfile.vue'
            ),
          meta: {
            title: 'Settings',
          },
        },
        {
          name: PathName.DocumentSettings,
          path: 'document-settings',
          component: () =>
            import(
              /* webpackChunkName: "settings" */ '../settings/document-settings/components/DocumentSettings.vue'
            ),
          meta: {
            title: 'Settings',
            access: {
              userRoles: [UserRoleLegacy.Manager, UserRoleLegacy.Admin],
              module: ModuleName.Contracts,
            },
          },
        },
        {
          name: PathName.SettingsReminder,
          path: 'reminder',
          component: () =>
            import(
              /* webpackChunkName: "settings" */ '../settings/reminders/components/ReminderSettings.vue'
            ),
          meta: {
            title: 'Settings',
            access: {
              userRoles: [UserRoleLegacy.Manager, UserRoleLegacy.Admin],
              module: ModuleName.Contracts,
            },
          },
        },
        {
          name: PathName.SettingsNotification,
          path: 'notification',
          component: () =>
            import(
              /* webpackChunkName: "settings" */ '../settings/notifications/components/NotificationSettings.vue'
            ),
          meta: {
            title: 'Settings',
            access: {
              userRoles: [UserRoleLegacy.Manager, UserRoleLegacy.Admin],
              module: ModuleName.Contracts,
            },
          },
        },
        {
          name: PathName.SettingsEntity,
          path: 'entity',
          component: () =>
            import(
              /* webpackChunkName: "settings" */ '../settings/entity/components/EntityInformation.vue'
            ),
          meta: {
            title: 'Settings',
            access: { userRoles: [UserRoleLegacy.Admin] },
          },
        },
        {
          name: PathName.SettingsEmailTemplates,
          path: 'email-templates',
          component: () =>
            import(
              /* webpackChunkName: "settings" */ '../settings/branding/email-templates/EmailTemplatesTable.vue'
            ),
          meta: {
            title: 'Settings',
            access: {
              userRoles: [UserRoleLegacy.Admin],
              features: [Feature.FullBranding],
            },
          },
        },
        {
          name: PathName.SettingsUsers,
          path: 'users',
          redirect: { name: PathName.ManageUsers },
        },
        {
          name: PathName.SettingsTeams,
          path: 'teams',
          component: () =>
            import(/* webpackChunkName: "settings" */ '../settings/teams/components/Teams.vue'),
          meta: {
            title: 'Settings',
            access: {
              userRoles: [UserRoleLegacy.Admin],
              features: [Feature.Teams],
            },
          },
        },
        {
          name: PathName.SettingsBranding,
          path: 'branding',
          component: () =>
            import(
              /* webpackChunkName: "settings" */ '../settings/branding/components/Branding.vue'
            ),
          meta: {
            title: 'Settings',
            access: {
              userRoles: [UserRoleLegacy.Admin],
              features: [Feature.BaseBranding],
            },
          },
        },
        {
          name: PathName.SettingsThemes,
          path: 'themes',
          component: () =>
            import(/* webpackChunkName: "settings" */ '../settings/themes/ThemesSettings.vue'),
          meta: {
            title: 'Settings',
            access: {
              userRoles: [UserRoleLegacy.Admin],
              features: [Feature.ExtendedBranding],
            },
          },
        },
        {
          name: PathName.SettingsTags,
          path: 'tags',
          component: () =>
            import(/* webpackChunkName: "settings" */ '../settings/tags/components/Tags.vue'),
          meta: {
            title: 'Settings',
            access: {
              userRoles: [UserRoleLegacy.Admin],
              features: [Feature.Tags],
              module: ModuleName.Contracts,
            },
          },
        },
        {
          name: PathName.SettingsIntegrations,
          path: 'integrations',
          meta: {
            title: 'Settings',
          },
          component: () =>
            import(
              /* webpackChunkName: "settings" */ '../settings/integrations/components/Integrations.vue'
            ),
        },
        {
          name: PathName.SettingsWebhooks,
          path: 'webhooks',
          component: () =>
            import(
              /* webpackChunkName: "settings" */ '../settings/webhooks/components/Webhooks.vue'
            ),
          meta: {
            title: 'Settings',
            access: {
              userRoles: [UserRoleLegacy.Admin],
              features: [Feature.Webhooks],
            },
          },
        },
        {
          name: PathName.SettingsSecurity,
          path: 'security',
          component: () =>
            import(
              /* webpackChunkName: "settings" */ '../settings/security/components/SecuritySettings.vue'
            ),
          meta: {
            title: 'Settings',
            access: { userRoles: [UserRoleLegacy.Admin] },
          },
        },
        {
          name: PathName.SettingsBilling,
          path: 'billing/:action?',
          component: () =>
            import('../settings/billing/components/OldSettingsSubscriptionBilling.vue'),
          meta: {
            title: 'Settings',
            access: {
              userRoles: [UserRoleLegacy.Admin],
            },
          },
          beforeEnter: (_, __, next) => {
            if (useLaunchDarkly().flags.value.newSubscription) {
              next({ name: PathName.SettingsSubscriptionNew });
            }
            next();
          },
        },
        {
          name: PathName.SettingsSubscription,
          path: 'subscription/:action?',
          component: () =>
            import('../settings/billing/components/OldSettingsSubscriptionBilling.vue'),
          meta: {
            title: 'Settings',
            access: {
              userRoles: [UserRoleLegacy.Admin],
            },
          },
          beforeEnter: (_, __, next) => {
            if (useLaunchDarkly().flags.value.newSubscription) {
              next({ name: PathName.SettingsSubscriptionNew });
            }
            next();
          },
        },
        {
          path: 'subscription-new',
          component: () =>
            import(
              /* webpackChunkName: "settings" */ '../settings/subscription/components/Subscription.vue'
            ),
          children: [
            {
              path: '',
              name: PathName.SettingsSubscriptionNew,
              component: () =>
                import(
                  /* webpackChunkName: "settings" */ '../settings/subscription/components/Overview.vue'
                ),
              meta: {
                title: 'Settings',
                access: {
                  userRoles: [UserRoleLegacy.Admin],
                },
              },
              beforeEnter: (_, __, next) => {
                next(
                  useLaunchDarkly().flags.value.newSubscription
                    ? undefined
                    : { name: PathName.SettingsSubscription }
                );
              },
            },
            {
              name: PathName.SettingsSubscriptionNewAddSeats,
              path: 'add-seats',
              component: () =>
                import(
                  /* webpackChunkName: "settings" */ '../subscription/add-seats/components/AddSeats.vue'
                ),
              meta: {
                title: 'Settings',
                access: {
                  userRoles: [UserRoleLegacy.Admin],
                  custom: () =>
                    useLaunchDarkly().flags.value.newSubscriptionSelfServe &&
                    !!useSubscriptionStore().purchasableSeatTypes.length &&
                    !useSessionStore().session.is_sub_entity,
                },
              },
            },
          ],
        },
        {
          name: PathName.Modules,
          path: 'modules',
          component: () =>
            import(
              /* webpackChunkName: "settings" */ '../settings/modules/components/SettingsModules.vue'
            ),
          meta: {
            title: 'Modules',
            access: {
              userRoles: [UserRoleLegacy.Admin],
              custom: () => !useLaunchDarkly().flags.value.newSubscription,
            },
          },
        },
        {
          name: PathName.CommunicationTemplates,
          path: 'communication-templates',
          component: () =>
            import('../communication-templates/components/CommunicationTemplates.vue'),
          meta: {
            title: 'Settings',
            access: { userRoles: [UserRoleLegacy.Admin], module: ModuleName.Contracts },
          },
        },
        {
          name: PathName.CommunicationTemplatesEdit,
          path: 'communication-templates/:id',
          component: () =>
            import('../communication-templates/components/CommunicationTemplatesEdit.vue'),
          meta: {
            title: 'Settings',
            access: { userRoles: [UserRoleLegacy.Admin], module: ModuleName.Contracts },
          },
        },
        {
          path: 'manage-users',
          component: () =>
            import(/* webpackChunkName: "settings" */ '../manage-users/components/ManageUsers.vue'),
          children: [
            {
              path: '',
              name: PathName.ManageUsers,
              component: () =>
                import(
                  /* webpackChunkName: "settings" */ '../manage-users/components/EntityList.vue'
                ),
              meta: {
                title: 'Settings',
                access: { userRoles: [UserRoleLegacy.Admin] },
              },
            },
            {
              name: PathName.EditUser,
              path: 'user/:id',
              component: () =>
                import(
                  /* webpackChunkName: "settings" */ '../manage-users/edit-user/components/EditUser.vue'
                ),
              props: true,
              meta: {
                title: 'Settings',
                access: { userRoles: [UserRoleLegacy.Admin] },
              },
            },
            {
              name: PathName.InviteUser,
              path: 'invite-user/:entityId',
              component: () =>
                import(
                  /* webpackChunkName: "settings" */ '../manage-users/components/InviteUser.vue'
                ),
              props: true,
              meta: {
                title: 'Settings',
                access: {
                  userRoles: [UserRoleLegacy.Admin],
                  custom: () => useLaunchDarkly().flags.value.newSubscription,
                },
              },
            },
            {
              name: PathName.ManageUsersAddSeats,
              path: 'add-seats',
              component: () =>
                import(
                  /* webpackChunkName: "settings" */ '../subscription/add-seats/components/AddSeats.vue'
                ),
              meta: {
                title: 'Settings',
                access: {
                  userRoles: [UserRoleLegacy.Admin],
                  custom: () =>
                    useLaunchDarkly().flags.value.newSubscriptionSelfServe &&
                    !!useSubscriptionStore().purchasableSeatTypes.length &&
                    !useSessionStore().session.is_sub_entity,
                },
              },
            },
          ],
        },
      ],
    },
    {
      name: PathName.MyDocumentsRedirect,
      path: '/my-documents-redirect',
      meta: {
        title: '',
        access: { module: ModuleName.Contracts },
      },
      component: () =>
        import(
          /* webpackChunkName: "my-documents-redirect" */ '../my-documents/components/MyDocumentsRedirect.vue'
        ),
    },
    {
      name: PathName.DrTemplate,
      path: '/dsr-edit/template/:id/:view?',
      meta: {
        title: 'GetAccept',
        access: { module: ModuleName.DealRooms },
      },
      beforeEnter: (to: Route, from: Route, next) => {
        if (from.name) {
          sessionStorage.removeItem('currentView');
        }
        to.meta.isComingFromApp = RouterHelper.isComingFromApp(from);
        next();
      },
      component: loadDealroomView,
      props: ({ params: { id, view }, query: { drawer } }: Route) => ({
        id,
        view,
        drawer,
      }),
    },
    {
      name: PathName.DrResource,
      path: '/dsr-edit/resource/:id',
      meta: {
        title: 'GetAccept',
        access: { module: ModuleName.DealRooms },
      },
      beforeEnter: (to: Route, from: Route, next) => {
        to.meta.isComingFromApp = RouterHelper.isComingFromApp(from);
        next();
      },
      component: loadDealroomView,
      props: ({ params: { id } }: Route) => ({
        id,
      }),
    },
    {
      name: PathName.DsrEdit,
      path: '/dsr-edit/:id/:view?/:tab?',
      meta: {
        title: 'GetAccept',
        access: { module: ModuleName.DealRooms },
      },
      beforeEnter: (to: Route, from: Route, next) => {
        if (from.name) {
          sessionStorage.removeItem('currentView');
          sessionStorage.removeItem(ChatConstants.draftStorageKeyEdit);
        }
        to.meta.isComingFromApp = RouterHelper.isComingFromApp(from);
        next();
      },
      component: loadDealroomView,
      props: ({ params: { id, view, tab }, query: { drawer } }: Route) => ({
        id,
        view,
        tab,
        drawer,
        locale: getLocaleFromCountryCode(i18n.locale),
      }),
    },

    {
      path: '/setup-admin/',
      name: PathName.SetupWizard,
      component: () =>
        import(
          /* webpackChunkName: "setup-wizard" */ '../onboarding/components/setup-wizard/SetupWizard.vue'
        ),
      meta: {
        title: 'Onboarding',
        access: { userRoles: [UserRoleLegacy.Admin], module: ModuleName.Contracts },
      },
    },
    {
      path: '/denied',
      name: PathName.InactiveUser,
      component: () =>
        import(
          /* webpackChunkName: "inactive-user-modal" */ '../manage-users/components/InactiveUserModal.vue'
        ),
    },
    {
      name: PathName.InactiveSubscription,
      path: '/inactive-subscription',
      component: () =>
        import(
          /* webpackChunkName: "inactive-subscription" */ '../subscription/components/InactiveSubscription.vue'
        ),

      beforeEnter: (_, __, next) => {
        if (!useSubscriptionStore().isInactiveSubscription) {
          next({ name: useRouterHelper().defaultPathName.value });
        }
        next();
      },
    },
    {
      path: '*',
      name: PathName.NotFound,
      component: () =>
        import(/* webpackChunkName: "not-found" */ '../components/not-found/NotFound.vue'),
    },
  ],
});

router.beforeEach(async (to, from, next) => {
  if (RouterHelper.isInitialNavigation(from)) {
    const { waitForIsLoaded: waitForRequiredDataLoaded } = useRequiredAppDataStore();
    await waitForRequiredDataLoaded();
  }
  const {
    hasRouteAccess,
    hasRouteAccessWithInactiveSubscription,
    isRedirectingFromMyDocuments,
  } = useRouterHelper();

  if (!hasRouteAccessWithInactiveSubscription(to)) {
    next({ name: PathName.InactiveSubscription });
    return;
  }

  if (!hasRouteAccess(to)) {
    const { toggleAccessDeniedModal } = useAccessDeniedModalStore();
    toggleAccessDeniedModal(true);
    next({ name: PathName.Root });
    return;
  }
  if (isRedirectingFromMyDocuments(to)) {
    next({ name: PathName.MyDocumentsRedirect });
    return;
  }
  next();
});

router.afterEach((to: Route) => {
  const modulesStore = useModulesStore();

  const moduleNameRouteMetaData = to.meta?.access?.module;
  if (moduleNameRouteMetaData) {
    modulesStore.setActiveModule(moduleNameRouteMetaData);
  }

  const title = to?.meta?.title;
  RouterHelper.setDocumentTitle(title);

  const isGoingToDsrEdit =
    to?.name === PathName.DsrEdit && to?.params?.id && to?.params?.type === 'dsr';

  const options = { screen: { height: window.innerHeight, width: window.innerWidth } };
  if (isGoingToDsrEdit) {
    Segment.page(
      'CoreApp',
      title,
      {
        dealroomId: to.params.id,
      },
      options
    );
    return;
  }

  Segment.page('CoreApp', title, {}, options);
});

export default router;
